import { useMemo, useState } from 'react'
import {
  Box, Button, Checkbox, Form, Grid, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle, Text,
} from '@ui'
import { ukDateKey } from '../format.js'

// GPX export (plan.md "Export walks as GPX"): the whole recorded route, home ends included.
const HOME_NOTE = 'Each file holds the whole route as recorded, including where it starts and ends (often home).'

// "Download GPX" on the walk page, for a walk with a GPS route.
export function GpxDownload({ walkId }) {
  return (
    <Box mb={3}>
      <Button size="sm" variant="secondary" outline href={`/api/walks/${walkId}/export.gpx`} download title={HOME_NOTE}>
        Download GPX
      </Button>
    </Box>
  )
}

// Which walks the server puts in the zip (cmd/server/export.go exportZip), so the dialog can count
// them: by UK start date, never excluded or test walks, and only counted ones unless `all`.
// Walks without a GPS route are counted here but left out of the zip (its README lists them).
export function exportedWalks(walks, from, to, all) {
  return walks.filter((w) => {
    const day = ukDateKey(w.start_time)
    return day >= from && day <= to && !w.excluded_from_analysis && !w.has_mock_location && (all || w.is_counted)
  })
}

// "Export GPX…" on the walks page: a date range (starting from the year filter, if any) as a zip.
export function GpxExportButton({ walks, year, everyRecording }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button size="sm" variant="secondary" outline onClick={() => setOpen(true)}>Export GPX…</Button>
      {open && (
        <GpxExportDialog walks={walks} year={year} everyRecording={everyRecording} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

function GpxExportDialog({ walks, year, everyRecording, onClose }) {
  const today = ukDateKey(new Date().toISOString())
  const thisYear = today.slice(0, 4)
  const y = year === 'all' ? thisYear : String(year)
  const [from, setFrom] = useState(`${y}-01-01`)
  const [to, setTo] = useState(y === thisYear ? today : `${y}-12-31`)
  const [all, setAll] = useState(everyRecording)
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to
  const count = useMemo(() => (valid ? exportedWalks(walks, from, to, all).length : 0), [walks, from, to, all, valid])
  const href = `/api/export/gpx.zip?from=${from}&to=${to}${all ? '&all=1' : ''}`

  return (
    <Modal show onHide={onClose} label="Export walks as GPX">
      <ModalHeader><ModalTitle>Export walks as GPX</ModalTitle></ModalHeader>
      <ModalBody>
        <Form onSubmit={(e) => e.preventDefault()}>
          <Grid gap={2} mb={3}>
            <Col xs={6}>
              <Label htmlFor="gpx-from" text="small">From</Label>
              <Input id="gpx-from" type="date" size="sm" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </Col>
            <Col xs={6}>
              <Label htmlFor="gpx-to" text="small">To</Label>
              <Input id="gpx-to" type="date" size="sm" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </Col>
          </Grid>
          <Checkbox
            id="gpx-all" mb={3} checked={all} onChange={(e) => setAll(e.target.checked)}
            label="Include every recording (other phones' and comparison apps' too)"
          />
        </Form>
        <Text as="p" mb={2}>
          {valid
            ? `${count.toLocaleString('en-GB')} ${all ? (count === 1 ? 'recording' : 'recordings') : (count === 1 ? 'walk' : 'walks')}, one GPX file each, in a zip.`
            : 'Choose a start date no later than the end date.'}
        </Text>
        <Text as="p" muted text="small" mb={0}>
          {HOME_NOTE} Excluded walks and test traces are left out, and so are walks typed in without a GPS
          route (the zip's README lists them).
        </Text>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" outline onClick={onClose}>Close</Button>
        <Button href={valid && count > 0 ? href : undefined} disabled={!valid || count === 0} download onClick={() => setTimeout(onClose, 0)}>
          Download zip
        </Button>
      </ModalFooter>
    </Modal>
  )
}
